import { get, includes, noop } from 'lodash'
import React, { useEffect, useRef } from 'react'
import { Col, Form, FormControl, Row } from 'react-bootstrap'

import { usePrevious } from '../../hooks'
import CountryPicker from '../CountryPicker'
import Field, {
  composeValidators,
  required,
  street_number_mustBeANumber,
  useField,
  useFieldContext,
} from '../Field'
import ZipInput from '../ZipInput'
import { mustBeValidCountryZip } from './helpers'

export interface TAddressPicker {
  name?: string
  direct?: boolean
  useShortCountryCode?: boolean
  browserAutocomplete?: boolean
  showRefs?: boolean
  labels?: {
    [key: string]: string
  }
  selectOnFocus?: boolean
  sm?: boolean
  showTooltips?: boolean
  dontNest?: boolean
  hiddenFields?: string[]
  showFields?: string[]
  largerCountry?: boolean
  readOnlyFields?: string[]
  showReference?: boolean
  skipZipCityClear?: boolean
  customZipRenderKey?: string // passed in from outside to enable revalidating on various onChange events to country and zip
  customTooltips?: {
    [key: string]: string
  }
  customComponents?: {
    [key: string]: string | JSX.Element
  }
}

const AddressPicker = (props: TAddressPicker): React.ReactElement => {
  const {
    name,
    direct,
    useShortCountryCode,
    browserAutocomplete,
    showRefs,
    labels = {},
    selectOnFocus,
    sm,
    showTooltips,
    dontNest,
    hiddenFields = [],
    showFields = [],
    largerCountry,
    readOnlyFields = [],
    showReference,
    skipZipCityClear = false,
    customZipRenderKey = undefined, // passed in from outside to enable revalidating on various onChange events to country and zip
    customTooltips,
    customComponents,
  } = props

  const { prefix = '' } = useFieldContext()

  const i18nLabels = {
    name1: i18n.t('name_1_label_new', `Name 1`),
    name2: i18n.t('name_2_label_new', `Name 2`),
    name3: i18n.t('name_3_label_new', `Name 3`),
    street: i18n.t('street_label_new', `Strasse`),
    streetNumber: i18n.t('number_label_dot_new', `Nr.`),
    streetNumberAddition: i18n.t('addition_label_dot_new', `Zus.`),
    zip: i18n.t('zip_label_new', `Plz`),
    country: i18n.t('country_label_new', `Land`),
    city: i18n.t('place_label_new', `Ort`),
    companyNumber: i18n.t('companyNumber_label_new', `Firmennummer`),
    branche: i18n.t('industry_label_new', `Branche`),
    representation: i18n.t('representation_label_new', `Vertretung`),
    ref1: i18n.t('reference_1_Label_long_new', `Referenz 1`),
    ref2: i18n.t('reference_2_label_long_new', `Referenz 2`),
    ref3: i18n.t('reference_3_label_long_new', `Referenz 3`),
    reference: i18n.t('reference_label_long_new', `Referenz`),
  }

  // reset zip/city when country changes
  const country = useField(dontNest ? 'country' : `${name}.country`)
  const [zip, setZip] = useField(dontNest ? 'zip' : `${name}.zip`, {
    asState: true,
  })
  const [, setCity] = useField(dontNest ? 'city' : `${name}.city`, {
    asState: true,
  })

  const isInitial = useRef(true)

  const previousCountry = usePrevious(country)
  const previousZip = usePrevious(zip)
  useEffect(() => {
    if (isInitial.current) {
      isInitial.current = false
      return
    }

    if (!skipZipCityClear) {
      if (previousCountry !== country && previousZip === zip) {
        if (global.addressPickerSkipZipCityClear > 0) {
          global.addressPickerSkipZipCityClear -= 1
          return
        }
        setZip('')
        setCity('')
      }
    }
  }, [country, zip])

  const label = {
    ...i18nLabels,
    ...labels,
  }

  const defaultTooltips = {
    name1: i18n.t(
      'ttCompanyName1_new',
      'Firmenname: Bitte notieren Sie hier den kompletten Firmennamen, wie er im Handelsregister eingtragen ist.',
    ),
    name2: i18n.t(
      'ttCompanyName2n3_new',
      `Name 2 und Name 3: Diese Felder ergänzen Sie bitte mit der Namenzusatzbezeichnung  (wie z.B. "Haus B") oder Abteilungen (wie z.B. "Werkstatt und Lager") Bitte setzen Sie keine Namen von Mitarbeitern ein. Diese Sollen in den nachfolgenden Kontaktdaten erfasst werden.`,
    ),
    name3: i18n.t(
      'ttCompanyName2n3_new',
      `Name 2 und Name 3: Diese Felder ergänzen Sie bitte mit der Namenzusatzbezeichnung  (wie z.B. "Haus B") oder Abteilungen (wie z.B. "Werkstatt und Lager") Bitte setzen Sie keine Namen von Mitarbeitern ein. Diese Sollen in den nachfolgenden Kontaktdaten erfasst werden.`,
    ),
    street: i18n.t(
      'ttCompanyStreet_new',
      `Strasse: Bitte setzen Sie hier die genaue Strassenbezeichnung ein. Wir verarbeiten Adressen automatisch und bei unrichtiger Angabe, können Positionsabweichungen entstehen`,
    ),
    streetNumber: i18n.t(
      'ttCompanyStreetNumber_new',
      `Hausnummer: Bitte erfassen Sie Ihre alphanumerische Hausnummer an dieser Stelle.`,
    ),
    streetNumberAddition: i18n.t(
      'ttCompanyStreetAddition_new',
      `Zusatz: Bitte setzen Sie hier Zusatzbezeichnungen zu Strassen oder Adressbezeichnungen ein wie z.B. «A» für die Strassennummerierung «123 A». Das «A» sollte im Zusatzfeld stehen.`,
    ),
    zip: i18n.t(
      'ttCompanyZip_new',
      `PLZ: Bitte geben Sie hier die exakte Postleitzahl ein. Es wird Ihnen eine Übersicht angezeigt. Sollte die PLZ in unserer Datenbank nicht vorhanden sein, wird auch kein Ortsname angezeigt und Sie müssen die Erfassung beider Felder (PLZ und Ort) manuell vornehmen und speichern.`,
    ),
  }

  const tooltips = {
    ...(showTooltips ? defaultTooltips : {}),
    ...customTooltips,
  }

  const zipField = dontNest ? (prefix ? `${prefix}.zip` : 'zip') : `${name}.zip`

  return (
    <div style={{ width: '100%' }}>
      <Row>
        <Col md={12}>
          <Form.Row>
            {!hiddenFields.includes('name1') && (
              <>
                {showReference && (
                  <Col md={2}>
                    <Field
                      name={dontNest ? 'reference' : `${name}.reference`}
                      label={`${label['reference']}`}
                      tooltip={tooltips['reference']}
                      selectOnFocus={selectOnFocus}
                      autoComplete={browserAutocomplete ? 'name' : 'none'}
                    >
                      <FormControl
                        readOnly={includes(readOnlyFields, 'reference')}
                        className="jump-target"
                        {...(sm ? { size: 'sm' } : {})}
                      />
                    </Field>
                  </Col>
                )}
                <Col md={showReference ? 10 : 12}>
                  <Field
                    name={dontNest ? 'name1' : `${name}.name1`}
                    label={`${label['name1']}`}
                    tooltip={tooltips['name1']}
                    validate={
                      includes(readOnlyFields, 'name1') ? noop : required
                    }
                    required={!includes(readOnlyFields, 'name1')}
                    selectOnFocus={selectOnFocus}
                    autoComplete={browserAutocomplete ? 'name' : 'none'}
                  >
                    {get(customComponents, 'name1') || (
                      <FormControl
                        readOnly={includes(readOnlyFields, 'name1')}
                        {...(sm ? { size: 'sm' } : {})}
                        className="jump-target"
                      />
                    )}
                  </Field>
                </Col>
              </>
            )}
            {!hiddenFields.includes('name2') && (
              <Col md={6}>
                <Field
                  name={dontNest ? 'name2' : `${name}.name2`}
                  label={label['name2']}
                  tooltip={tooltips['name2']}
                  selectOnFocus={selectOnFocus}
                  {...(sm ? { size: 'sm' } : {})}
                >
                  <FormControl
                    readOnly={includes(readOnlyFields, 'name2')}
                    className="jump-target"
                  />
                </Field>
              </Col>
            )}
            {!hiddenFields.includes('name3') && (
              <Col md={6}>
                <Field
                  name={dontNest ? 'name3' : `${name}.name3`}
                  label={label['name3']}
                  tooltip={tooltips['name3']}
                  selectOnFocus={selectOnFocus}
                >
                  <FormControl
                    readOnly={includes(readOnlyFields, 'name3')}
                    className="jump-target"
                    {...(sm ? { size: 'sm' } : {})}
                  />
                </Field>
              </Col>
            )}
            {!hiddenFields.includes('street') && (
              <Col md={largerCountry ? 7 : 8}>
                <Field
                  name={dontNest ? 'street' : `${name}.street`}
                  validate={
                    includes(readOnlyFields, 'street')
                      ? noop
                      : composeValidators(required)
                  }
                  required={!includes(readOnlyFields, 'street')}
                  tooltip={tooltips['street']}
                  selectOnFocus={selectOnFocus}
                  label={label['street']}
                  autoComplete={browserAutocomplete ? 'address-line1' : 'none'}
                >
                  <FormControl
                    readOnly={includes(readOnlyFields, 'street')}
                    className="jump-target"
                    {...(sm ? { size: 'sm' } : {})}
                  />
                </Field>
              </Col>
            )}
            {!hiddenFields.includes('streetNumber') && (
              <Col md={largerCountry ? 3 : 2}>
                <Field
                  name={dontNest ? 'streetNumber' : `${name}.streetNumber`}
                  validate={
                    includes(readOnlyFields, 'streetNumber')
                      ? noop
                      : street_number_mustBeANumber
                  }
                  tooltip={tooltips['streetNumber']}
                  selectOnFocus={selectOnFocus}
                  label={label['streetNumber']}
                >
                  <FormControl
                    readOnly={includes(readOnlyFields, 'streetNumber')}
                    className="jump-target"
                    {...(sm ? { size: 'sm' } : {})}
                  />
                </Field>
              </Col>
            )}
            {!hiddenFields.includes('streetNumberAddition') && (
              <Col md={2}>
                <Field
                  name={
                    dontNest
                      ? 'streetNumberAddition'
                      : `${name}.streetNumberAddition`
                  }
                  label={label['streetNumberAddition']}
                  tooltip={tooltips['streetNumberAddition']}
                  selectOnFocus={selectOnFocus}
                  {...(sm ? { size: 'sm' } : {})}
                >
                  <FormControl
                    readOnly={includes(readOnlyFields, 'streetNumberAddition')}
                    className="jump-target"
                  />
                </Field>
              </Col>
            )}
            {!hiddenFields.includes('country') && (
              <Col md={largerCountry ? 4 : 3}>
                <CountryPicker
                  field={dontNest ? 'country' : `${name}.country`}
                  validateFields={[zipField]}
                  label={label['country']}
                  validate={
                    includes(readOnlyFields, 'country') ? noop : required
                  }
                  required={!includes(readOnlyFields, 'country')}
                  // direct={direct}
                  direct
                  useShortCode={useShortCountryCode}
                  small={sm || false}
                  autoComplete={browserAutocomplete ? 'country' : 'none'}
                  disabled={includes(readOnlyFields, 'country')}
                />
              </Col>
            )}
            {!hiddenFields.includes('zip') && (
              <Col md={3}>
                <Field
                  name={dontNest ? 'zip' : `${name}.zip`}
                  label={label['zip']}
                  validate={
                    includes(readOnlyFields, 'zip')
                      ? noop
                      : composeValidators(
                          required,
                          mustBeValidCountryZip(prefix),
                        )
                  }
                  required={!includes(readOnlyFields, 'zip')}
                  tooltip={tooltips['zip']}
                  autoComplete={browserAutocomplete ? 'postal-code' : 'none'}
                >
                  <ZipInput
                    {...(sm ? { size: 'sm' } : {})}
                    country={country}
                    onChangeCity={setCity}
                    selectOnFocus={selectOnFocus}
                    readOnly={includes(readOnlyFields, 'zip')}
                    customZipRenderKey={customZipRenderKey}
                  />
                </Field>
              </Col>
            )}
            {!hiddenFields.includes('city') && (
              <Col md={largerCountry ? 5 : 6}>
                <Field
                  name={dontNest ? 'city' : `${name}.city`}
                  label={label['city']}
                  validate={includes(readOnlyFields, 'city') ? noop : required}
                  required={!includes(readOnlyFields, 'zip')}
                  selectOnFocus={selectOnFocus}
                  autoComplete={browserAutocomplete ? 'address-level2' : 'none'}
                >
                  <FormControl
                    {...(sm ? { size: 'sm' } : {})}
                    readOnly={includes(readOnlyFields, 'city')}
                    className="jump-target"
                  />
                </Field>
              </Col>
            )}
            {showFields.includes('companyNumber') && (
              <Col md={4}>
                <Field
                  name={dontNest ? 'companyNumber' : `${name}.companyNumber`}
                  label={label['companyNumber']}
                  selectOnFocus={selectOnFocus}
                >
                  <FormControl {...(sm ? { size: 'sm' } : {})} />
                </Field>
              </Col>
            )}
            {showFields.includes('branche') && (
              <Col md={4}>
                <Field
                  name={dontNest ? 'branche' : `${name}.branche`}
                  label={label['branche']}
                  selectOnFocus={selectOnFocus}
                >
                  <FormControl {...(sm ? { size: 'sm' } : {})} />
                </Field>
              </Col>
            )}
            {showFields.includes('representation') && (
              <Col md={4}>
                <Field
                  name={dontNest ? 'representation' : `${name}.representation`}
                  label={label['representation']}
                  selectOnFocus={selectOnFocus}
                >
                  <FormControl {...(sm ? { size: 'sm' } : {})} />
                </Field>
              </Col>
            )}
          </Form.Row>
        </Col>
      </Row>
      {showRefs && (
        <React.Fragment>
          <Form.Row>
            <Col>
              <Field
                name={dontNest ? 'ref1' : `${name}.ref1`}
                label={i18n.t('references', 'Referenzen')}
                selectOnFocus={selectOnFocus}
              >
                <FormControl placeholder={label['ref1']} />
              </Field>
            </Col>

            <Col>
              <Field
                name={dontNest ? 'ref2' : `${name}.ref2`}
                selectOnFocus={selectOnFocus}
                hideLabel
                label=" "
              >
                <FormControl placeholder={label['ref2']} />
              </Field>
            </Col>

            <Col>
              <Field
                name={dontNest ? 'ref3' : `${name}.ref3`}
                selectOnFocus={selectOnFocus}
                hideLabel
                label=" "
              >
                <FormControl placeholder={label['ref3']} />
              </Field>
            </Col>
          </Form.Row>
        </React.Fragment>
      )}
    </div>
  )
}

export default AddressPicker

export function parseAddress(value: { [key: string]: string }): {
  [key: string]: string
} | void {
  if (!value) return
  return {
    ...value,
    // country: parseCountry(value.country)
  }
}

export function stringifyAddress(value: { [key: string]: string }): {
  [key: string]: string
} | void {
  if (!value) return
  return {
    ...value,
    // country: stringifyCountry(value.country)
  }
}

export { mustBeValidCountryZip }
